import React, { useState, useMemo, useEffect } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  Button,
  Paper,
} from "@mui/material";
import { useGetListOfBugsQuery } from "@/features/bughurd/api";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { ApexOptions } from "apexcharts";
import { useParams } from "react-router-dom";

interface WorkloadSummaryDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedDate?: Date | null;
  sprint?: any;
  parentData?: any;
}

export const WorkloadSummaryDrawer: React.FC<WorkloadSummaryDrawerProps> = ({
  open,
  onClose,
  selectedDate,
  sprint,
  parentData,
}) => {
  const { id } = useParams();
  const theme = useTheme();
  const [viewType, setViewType] = useState<"daily" | "weekly">("daily");
  const [internalSelectedDate, setInternalSelectedDate] = useState<Date | null>(
    selectedDate || null
  );

  const formatDateForAPI = (date: Date | null | undefined): string => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formattedDateForAPI = useMemo(() => {
    return formatDateForAPI(internalSelectedDate);
  }, [internalSelectedDate]);

  const {
    data: workloadData,
    isLoading: isBugsLoading,
    refetch: refetchBugs,
  } = useGetListOfBugsQuery(
    {
      id: id,
      forBoard: true,
      sprint: sprint?._id,
      viewType: "WORKLOAD_SUMMARY_DAYS",
      summaryDate: formattedDateForAPI,
      page: 1,
      limit: 10,
    },
    {
      skip: !open || !formattedDateForAPI || !sprint?._id,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (selectedDate) {
      setInternalSelectedDate(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (open && formattedDateForAPI && sprint?._id) {
      refetchBugs();
    }
  }, [formattedDateForAPI, open, sprint?._id, refetchBugs]);

  const sprintStartDate = sprint?.startDate ? new Date(sprint.startDate) : null;
  const sprintEndDate = sprint?.endDate ? new Date(sprint.endDate) : null;

  // Extract workload summary from API data
  const dayData = useMemo(() => {
    const items = workloadData?.data?.decryptedData?.items || [];
    const selectedDateStr = formatDateForAPI(internalSelectedDate);
    return items.find((item: any) => item._id === selectedDateStr) || null;
  }, [workloadData, internalSelectedDate]);

  // Calculate workload summary from API data
  const workloadSummary = useMemo(() => {
    if (!dayData) {
      return {
        totalMembers: `0/0`,
        totalAssignedTasks: 0,
        actualPlannedHours: 0,
        totalBurnedHours: 0,
        totalPendingTasks: 0,
      };
    }

    return {
      totalMembers: `${dayData.totalAssignees || 0}/${
        sprint?.assignTo?.length || 0
      }`,
      totalAssignedTasks: dayData.totalBugs || 0,
      actualPlannedHours: dayData.totalPlannedEstimationHours || 0,
      totalBurnedHours: dayData.totalBurnedHours || 0,
      totalPendingTasks: dayData.totalPendingTasks || 0,
    };
  }, [dayData, sprint]);

  // Transform skill set data for chart
  const teamWiseData = useMemo(() => {
    if (!dayData || !dayData.skillSetTimeSpent) {
      return {
        categories: [],
        data: [],
        fullNames: [],
      };
    }

    const skillData = dayData.skillSetTimeSpent.map((skill: any) => ({
      name: skill.skillSet,
      hours: skill.totalHours,
      fullName: skill.skillSet.replace(/_/g, " "),
    }));

    return {
      categories: skillData.map((s: any) => s.name),
      data: skillData.map((s: any) => s.hours),
      fullNames: skillData.map((s: any) => s.fullName),
    };
  }, [dayData]);

  // Calculate total burned hours for chart caption
  const totalBurnedHours = useMemo(() => {
    return teamWiseData.data.reduce(
      (sum: number, hours: number) => sum + hours,
      0
    );
  }, [teamWiseData]);

  // ApexChart Options for Burn Rate
  const burnRateChartOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      width: "100%",
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        distributed: false,
        columnWidth: "60%",
      },
    },
    colors: ["#06B6D4"],
    xaxis: {
      categories: teamWiseData.categories,
      labels: {
        rotate: -45,
        style: {
          fontSize: "11px",
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      title: {
        text: "Total Hours",
        style: {
          fontSize: "12px",
        },
      },
      labels: {
        style: {
          fontSize: "11px",
        },
        formatter: function (val: number) {
          return Math.floor(val).toString();
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    grid: {
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const value = series[seriesIndex][dataPointIndex];
        const fullName = teamWiseData.fullNames[dataPointIndex];
        return `<div style="padding: 8px 12px; background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
          <div style="font-size: 12px; font-weight: 600; margin-bottom: 4px;">${fullName}</div>
          <div style="font-size: 14px; color: #06B6D4; font-weight: 700;">${value}H</div>
        </div>`;
      },
    },
  };

  const handleViewTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewType: "daily" | "weekly" | null
  ) => {
    if (newViewType !== null) {
      setViewType(newViewType);
    }
  };

  const formatDate = (date: Date | null | undefined) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateShort = (date: Date | null | undefined) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "numeric",
    });
  };

  const { calendarDates, daysOfWeek } = useMemo(() => {
    if (!sprintStartDate || !sprintEndDate) {
      return {
        calendarDates: [],
        daysOfWeek: [],
      };
    }

    const start = new Date(sprintStartDate);
    const end = new Date(sprintEndDate);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const dates: Date[] = [];
    const currentDate = new Date(start);

    while (currentDate <= end) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    const daysLabels = dates.map((date) => {
      const dayNames = ["S", "M", "T", "W", "T", "F", "S"];
      return dayNames[date.getDay()];
    });

    return {
      calendarDates: dates,
      daysOfWeek: daysLabels,
    };
  }, [sprintStartDate, sprintEndDate]);

  const handleDateClick = (date: Date) => {
    setInternalSelectedDate(date);
  };

  const isSameDay = (date1: Date | null, date2: Date | null) => {
    if (!date1 || !date2) return false;
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return isSameDay(date, today);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480, md: 550 },
          bgcolor: theme.palette.background.default,
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {sprint?.name || "Sprint Workload"}
            </Typography>
            {sprintStartDate && sprintEndDate && (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary }}
              >
                ({formatDateShort(sprintStartDate)} -{" "}
                {formatDateShort(sprintEndDate)})
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              sx={{
                textTransform: "none",
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                borderRadius: 0,
                color: theme.palette.primary.main,
                fontWeight: 600,
                pb: 1,
              }}
            >
              Work Load Summary
            </Button>
          </Box>

          {/* Calendar */}
          {calendarDates.length > 0 && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${Math.min(
                    calendarDates.length,
                    7
                  )}, 1fr)`,
                  mb: 1,
                  px: 1,
                  maxWidth: "100%",
                }}
              >
                {daysOfWeek
                  .slice(0, Math.min(calendarDates.length, 7))
                  .map((day, index) => (
                    <Box key={index} sx={{ textAlign: "center" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: 10,
                          fontWeight: 500,
                        }}
                      >
                        {day}
                      </Typography>
                    </Box>
                  ))}
              </Box>

              <Box sx={{ mb: 3, px: 1 }}>
                {Array.from({
                  length: Math.ceil(calendarDates.length / 7),
                }).map((_, rowIndex) => (
                  <Box
                    key={rowIndex}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7, 1fr)",
                      mb: 0.5,
                    }}
                  >
                    {calendarDates
                      .slice(rowIndex * 7, (rowIndex + 1) * 7)
                      .map((date, colIndex) => {
                        const isSelected = isSameDay(
                          date,
                          internalSelectedDate
                        );
                        const isTodayDate = isToday(date);

                        return (
                          <Box
                            key={`${rowIndex}-${colIndex}`}
                            onClick={() => handleDateClick(date)}
                            sx={{
                              width: "100%",
                              aspectRatio: "1",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "50%",
                              bgcolor: isSelected
                                ? theme.palette.primary.main
                                : isTodayDate
                                ? "#FF6B6B"
                                : "transparent",
                              color:
                                isSelected || isTodayDate ? "white" : "inherit",
                              cursor: "pointer",
                              fontWeight: isSelected || isTodayDate ? 600 : 400,
                              fontSize: "0.875rem",
                              transition: "all 0.2s",
                              "&:hover": {
                                bgcolor: isSelected
                                  ? theme.palette.primary.dark
                                  : isTodayDate
                                  ? "#FF5252"
                                  : theme.palette.action.hover,
                                transform: "scale(1.05)",
                              },
                            }}
                          >
                            {date.getDate()}
                          </Box>
                        );
                      })}
                    {rowIndex === Math.ceil(calendarDates.length / 7) - 1 &&
                      calendarDates.length % 7 !== 0 &&
                      Array.from({
                        length: 7 - (calendarDates.length % 7),
                      }).map((_, emptyIndex) => (
                        <Box
                          key={`empty-${emptyIndex}`}
                          sx={{ aspectRatio: "1" }}
                        />
                      ))}
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Workload Summary Card */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Work Load Summary {formatDateShort(internalSelectedDate)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Daily workload metrics
                </Typography>
              </Box>
              <IconButton size="small">
                <MoreHorizIcon fontSize="small" />
              </IconButton>
            </Box>

            {isBugsLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 4,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary, mb: 0.5 }}
                  >
                    Total Members
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {workloadSummary.totalMembers}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        width: 4,
                        height: 20,
                        bgcolor: "#10B981",
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Total Assigned Tasks
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {workloadSummary.totalAssignedTasks}
                    </Typography>
                  </Box>

                  <Box>
                    <Box
                      sx={{
                        width: 4,
                        height: 20,
                        bgcolor: "#3B82F6",
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Total Pending Tasks
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {workloadSummary.totalPendingTasks}
                    </Typography>
                  </Box>

                  <Box>
                    <Box
                      sx={{
                        width: 4,
                        height: 20,
                        bgcolor: "#F59E0B",
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Total Burned hours
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {workloadSummary.totalBurnedHours}
                    </Typography>
                  </Box>

                  <Box>
                    <Box
                      sx={{
                        width: 4,
                        height: 20,
                        bgcolor: "#EF4444",
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Actual planned hours
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {workloadSummary.actualPlannedHours}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Burn Rate Chart */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Burn Rate team wise {formatDateShort(internalSelectedDate)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Hours logged by skill set
                </Typography>
              </Box>
              <IconButton size="small">
                <MoreHorizIcon fontSize="small" />
              </IconButton>
            </Box>

            {isBugsLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 250,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Loading chart...
                </Typography>
              </Box>
            ) : teamWiseData.data.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 250,
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No time logged for this date
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ width: "100%", height: 250 }}>
                  {parentData?.chartWrapper ? (
                    <parentData.chartWrapper
                      options={burnRateChartOptions}
                      series={[{ name: "Hours", data: teamWiseData.data }]}
                      type="bar"
                      height={250}
                      width="100%"
                    />
                  ) : null}
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    color: theme.palette.text.secondary,
                    mt: 1,
                  }}
                >
                  Total Burned hours ({totalBurnedHours}H)
                </Typography>
              </>
            )}
          </Paper>
        </Box>
      </Box>
    </Drawer>
  );
};

export default WorkloadSummaryDrawer;
